import React, { useState } from "react";
import {
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  Chip,
  Divider,
  Alert,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import BarChartIcon from "@mui/icons-material/BarChart";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import BalanceIcon from "@mui/icons-material/Balance";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import SecurityIcon from "@mui/icons-material/Security";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import { useCountry } from "../context/CountryContext";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

const ComponentCard: React.FC<{
  name: string;
  weight: number;
  icon: React.ReactElement;
  color: string;
  description: string;
  details?: string[];
}> = ({ name, weight, icon, color, description, details }) => (
  <Accordion
    sx={{
      mb: 2,
      boxShadow: "none",
      "&:before": { display: "none" },
      border: "1px solid #e0e0e0",
      borderRadius: 2,
    }}
  >
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      <Box
        sx={{ display: "flex", alignItems: "center", width: "100%", gap: 2 }}
      >
        <Box sx={{ color, minWidth: 40 }}>{icon}</Box>
        <Typography sx={{ flexGrow: 1, fontWeight: "bold" }}>{name}</Typography>
        <Chip
          label={`${weight}%`}
          sx={{ bgcolor: color, color: "white", fontWeight: "bold" }}
        />
      </Box>
    </AccordionSummary>
    <AccordionDetails sx={{ borderTop: "1px solid #e0e0e0", pt: 2 }}>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 2 }}
      >
        {description}
      </Typography>
      {details && (
        <List
          dense
          sx={{ bgcolor: "#f5f5f5", borderRadius: 1, p: 1 }}
        >
          {details.map((detail, i) => (
            <ListItem
              key={i}
              sx={{ py: 0.5 }}
            >
              <ListItemText
                primary={detail}
                primaryTypographyProps={{
                  variant: "body2",
                  color: "text.secondary",
                }}
              />
            </ListItem>
          ))}
        </List>
      )}
    </AccordionDetails>
  </Accordion>
);

const Methodology: React.FC = () => {
  const { country } = useCountry();
  const [tabValue, setTabValue] = useState(
    country === "gb" ? 0 : country === "ie" ? 1 : 2
  );

  React.useEffect(() => {
    setTabValue(country === "gb" ? 0 : country === "ie" ? 1 : 2);
  }, [country]);

  return (
    <Box sx={{ p: 4, bgcolor: "background.default", minHeight: "100vh" }}>
      <Typography
        variant="h4"
        component="h1"
        sx={{ fontWeight: "bold", mb: 2, color: "text.primary" }}
      >
        Scoring Methodology
      </Typography>
      <Typography
        variant="subtitle1"
        color="text.secondary"
        sx={{ mb: 4 }}
      >
        Our comprehensive approach to evaluating gender pay equity across the
        UK, Ireland, and France
      </Typography>

      {/* Executive Summary */}
      <Card
        elevation={3}
        sx={{ mb: 4, borderRadius: 3 }}
      >
        <CardContent sx={{ p: 3 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", mb: 3 }}
          >
            Executive Summary
          </Typography>
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                  <TableCell sx={{ fontWeight: "bold" }}>Aspect</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    United Kingdom
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Ireland</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>France</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>Companies</TableCell>
                  <TableCell>15,000+</TableCell>
                  <TableCell>895</TableCell>
                  <TableCell>39,848</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>Scored</TableCell>
                  <TableCell>~12,750 (85%)</TableCell>
                  <TableCell>895 (100%)</TableCell>
                  <TableCell>28,389 (71%)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>Years</TableCell>
                  <TableCell>2017-2024 (8 years)</TableCell>
                  <TableCell>2022-2024 (3 years)</TableCell>
                  <TableCell>2018-2024 (7 years)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>Approach</TableCell>
                  <TableCell>Multi-component</TableCell>
                  <TableCell>Multi-component</TableCell>
                  <TableCell>Government score + trend</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>Components</TableCell>
                  <TableCell>8 (or 6 fallback)</TableCell>
                  <TableCell>6</TableCell>
                  <TableCell>2 elements</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>Score Range</TableCell>
                  <TableCell>0-100</TableCell>
                  <TableCell>0-100</TableCell>
                  <TableCell>0-100</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>Grading</TableCell>
                  <TableCell>Percentile (A-F)</TableCell>
                  <TableCell>Percentile (A-F)</TableCell>
                  <TableCell>Percentile (A-F)</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Grading System */}
      <Card
        elevation={3}
        sx={{ mb: 4, borderRadius: 3 }}
      >
        <CardContent sx={{ p: 3 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", mb: 3 }}
          >
            Letter Grade System (All Countries)
          </Typography>
          <Alert
            severity="info"
            sx={{ mb: 3 }}
          >
            Grades are assigned based on percentile ranking within each
            country's dataset, with an absolute safety threshold
          </Alert>
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                  <TableCell sx={{ fontWeight: "bold" }}>Grade</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Percentile</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    Absolute Safety
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Meaning</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <Chip
                      label="A"
                      color="success"
                      sx={{ fontWeight: "bold" }}
                    />
                  </TableCell>
                  <TableCell>90th+</TableCell>
                  <TableCell>Score ≥30</TableCell>
                  <TableCell>Excellent - Top 10% performers</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Chip
                      label="B"
                      sx={{
                        bgcolor: "#4caf50",
                        color: "white",
                        fontWeight: "bold",
                      }}
                    />
                  </TableCell>
                  <TableCell>75-89th</TableCell>
                  <TableCell>Score ≥30</TableCell>
                  <TableCell>Good - Top 25% performers</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Chip
                      label="C"
                      sx={{
                        bgcolor: "#ff9800",
                        color: "white",
                        fontWeight: "bold",
                      }}
                    />
                  </TableCell>
                  <TableCell>50-74th</TableCell>
                  <TableCell>Score ≥30</TableCell>
                  <TableCell>Average - Middle performers</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Chip
                      label="D"
                      sx={{
                        bgcolor: "#ff5722",
                        color: "white",
                        fontWeight: "bold",
                      }}
                    />
                  </TableCell>
                  <TableCell>25-49th</TableCell>
                  <TableCell>Score ≥30</TableCell>
                  <TableCell>Below Average - Bottom 50%</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Chip
                      label="F"
                      color="error"
                      sx={{ fontWeight: "bold" }}
                    />
                  </TableCell>
                  <TableCell>&lt;25th OR</TableCell>
                  <TableCell>Score &lt;30</TableCell>
                  <TableCell>Poor - Bottom 25% or failing threshold</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 2 }}
          >
            <strong>Safety Check:</strong> Any score below 30 automatically
            receives an F grade, regardless of percentile ranking.
          </Typography>
        </CardContent>
      </Card>

      {/* Country-Specific Tabs */}
      <Card
        elevation={3}
        sx={{ borderRadius: 3 }}
      >
        <Box
          sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "#f5f5f5" }}
        >
          <Tabs
            value={tabValue}
            onChange={(_, newValue) => setTabValue(newValue)}
            sx={{ px: 2 }}
          >
            <Tab label="United Kingdom" />
            <Tab label="Ireland" />
            <Tab label="France" />
          </Tabs>
        </Box>

        {/* UK Tab */}
        <TabPanel
          value={tabValue}
          index={0}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography
              variant="h5"
              sx={{ fontWeight: "bold", mb: 3 }}
            >
              United Kingdom Methodology
            </Typography>

            <Alert
              severity="info"
              sx={{ mb: 3 }}
            >
              The UK uses two methodologies:{" "}
              <strong>Full (8 components)</strong> for companies with valid
              industry codes (~85%), and{" "}
              <strong>Fallback (6 components)</strong> for companies without
              industry data (~15%).
            </Alert>

            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", mb: 2 }}
            >
              Full Methodology (8 Components)
            </Typography>

            <ComponentCard
              name="1. Trend Analysis"
              weight={30}
              icon={<TrendingUpIcon />}
              color="#2196f3"
              description="Analyzes historical improvement trajectory across years. Companies with consistent pay gap reduction are rewarded."
              details={[
                "Composite Gap = Mean Hourly (42%) + Median Hourly (28%) + Mean Bonus (18%) + Median Bonus (12%)",
                "3+ years: Full analysis with 5 metrics (slope, consistency, acceleration, volatility, recent performance)",
                "2 years: Simple improvement scoring (30-70 points)",
                "1 year: Industry percentile (max 80 points)",
                "COVID years (2020-21) weighted at 0.7x",
                "Bonus for 6+ years of data: ×1.05 multiplier",
              ]}
            />

            <ComponentCard
              name="2. Current Performance"
              weight={21}
              icon={<BarChartIcon />}
              color="#4caf50"
              description="Benchmarks recent pay gap against industry peers. Lower gap = higher score."
              details={[
                "Industry percentile ranking (requires 30+ industry peers)",
                "Bonuses: Gap ≤5% (+15pts), Gap ≤10% (+10pts)",
                "Penalty: Gap ≥25% (-15pts)",
                "Fallback to absolute scoring if <30 industry peers",
              ]}
            />

            <ComponentCard
              name="3. Industry Leadership"
              weight={15}
              icon={<BusinessCenterIcon />}
              color="#ff9800"
              description="Sector benchmarking and COVID resilience assessment."
              details={[
                "Top 25% in industry (Q1): 90 points",
                "Above median: 70 points",
                "Below median: 40 points",
                "COVID Resilience Bonus: Improved 2019→2022/2023 (+10pts)",
              ]}
            />

            <ComponentCard
              name="4. Structural Equity"
              weight={11}
              icon={<BalanceIcon />}
              color="#9c27b0"
              description="Gender-neutral organizational equity through bonus parity and quartile distribution."
              details={[
                "Bonus Participation (40%): Equal access to bonuses",
                "Quartile Consistency (60%): Balanced gender distribution across pay levels",
                "Perfect parity (≤5% gap): 100 points",
                "Top quartile bonus for minimal deviation",
              ]}
            />

            <ComponentCard
              name="5. Reporting Quality"
              weight={9}
              icon={<VerifiedUserIcon />}
              color="#00bcd4"
              description="Rewards consistency and completeness in reporting."
              details={[
                "Large companies (≥6 years): Base score up to 70pts",
                "Small companies (<6 years, voluntary): Base up to 80pts + 20pts bonus",
                "Gap penalty: -5pts per missing year",
                "COVID adjustment: +5pts for missing 2020/2021",
              ]}
            />

            <ComponentCard
              name="6. Leadership Engagement"
              weight={8}
              icon={<SupervisorAccountIcon />}
              color="#e91e63"
              description="Seniority of responsible person indicates top-level accountability."
              details={[
                "5-Level hierarchy: CEO/President (100pts), C-suite (85pts), CHRO/VP (70pts), Director (50pts), Manager (30pts)",
                "Hybrid scoring: 60% recent (last 2 years) + 40% historical",
                "Bonuses: 3+ years C-suite (+20pts), Improving trajectory (+15pts)",
              ]}
            />

            <ComponentCard
              name="7. Leadership Stability"
              weight={3}
              icon={<SecurityIcon />}
              color="#795548"
              description="Penalizes excessive turnover in responsible persons."
              details={[
                "Counts level changes over time",
                "Major changes (±2 levels): -15pts each",
                "Bonus: Stable high engagement (+20pts)",
              ]}
            />

            <ComponentCard
              name="8. Creative Signals"
              weight={3}
              icon={<LightbulbIcon />}
              color="#607d8b"
              description="Detects transparency indicators through URL quality, submission timing, and data completeness."
              details={[
                "URL Quality: Simple URLs (+10pts), Transparency keywords (+8pts), Complex URLs (-5pts)",
                "Submission Timing: ≥80% on-time (+12pts), Frequently late (-8pts)",
                "Leadership Consistency: Sustained C-suite (+10pts)",
                "Data Completeness: Exceptional disclosure (+8pts)",
              ]}
            />

            <Divider sx={{ my: 4 }} />

            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", mb: 2 }}
            >
              Fallback Methodology (6 Components)
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2 }}
            >
              For companies without valid SIC codes. Industry-dependent
              components are removed and weights are rebalanced:
            </Typography>
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ borderRadius: 2 }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                    <TableCell sx={{ fontWeight: "bold" }}>Component</TableCell>
                    <TableCell
                      align="center"
                      sx={{ fontWeight: "bold" }}
                    >
                      Full
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ fontWeight: "bold" }}
                    >
                      Fallback
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ fontWeight: "bold" }}
                    >
                      Change
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Trend Analysis</TableCell>
                    <TableCell align="center">30%</TableCell>
                    <TableCell align="center">35%</TableCell>
                    <TableCell align="center">+5%</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Current Performance</TableCell>
                    <TableCell align="center">21%</TableCell>
                    <TableCell align="center">25%</TableCell>
                    <TableCell align="center">+4%</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Reporting Quality</TableCell>
                    <TableCell align="center">9%</TableCell>
                    <TableCell align="center">15%</TableCell>
                    <TableCell align="center">+6%</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Leadership Engagement</TableCell>
                    <TableCell align="center">8%</TableCell>
                    <TableCell align="center">10%</TableCell>
                    <TableCell align="center">+2%</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Data Quality</TableCell>
                    <TableCell align="center">4%</TableCell>
                    <TableCell align="center">8%</TableCell>
                    <TableCell align="center">+4%</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Creative Signals</TableCell>
                    <TableCell align="center">3%</TableCell>
                    <TableCell align="center">7%</TableCell>
                    <TableCell align="center">+4%</TableCell>
                  </TableRow>
                  <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                    <TableCell sx={{ fontWeight: "bold" }}>TOTAL</TableCell>
                    <TableCell
                      align="center"
                      sx={{ fontWeight: "bold" }}
                    >
                      —
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ fontWeight: "bold" }}
                    >
                      100%
                    </TableCell>
                    <TableCell align="center">—</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 2 }}
            >
              <strong>Removed components:</strong> Industry Leadership (15%),
              Structural Equity (11%), Leadership Stability (3%)
            </Typography>
          </CardContent>
        </TabPanel>

        {/* Ireland Tab */}
        <TabPanel
          value={tabValue}
          index={1}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography
              variant="h5"
              sx={{ fontWeight: "bold", mb: 3 }}
            >
              Ireland Methodology
            </Typography>

            <Alert
              severity="success"
              sx={{ mb: 3 }}
            >
              All 895 Irish companies have valid SIC codes (text-based). No
              fallback methodology needed.
            </Alert>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 3 }}
            >
              Similar to UK but streamlined for Ireland's smaller dataset (3
              years: 2022-2024). Leadership tracking unavailable, so weights are
              redistributed across 6 components.
              <br />
              Thank you to{" "}
              <a
                href="https://paygap.ie"
                target="_blank"
                rel="noopener noreferrer"
              >
                paygap.ie
              </a>{" "}
              for providing a cleaned dataset.
            </Typography>

            <ComponentCard
              name="1. Trend Analysis"
              weight={32}
              icon={<TrendingUpIcon />}
              color="#2196f3"
              description="Same 3-path logic as UK. No COVID weighting (data starts post-pandemic in 2022)."
              details={[
                "Composite Gap formula identical to UK",
                "3 years: Full analysis with 5 metrics",
                "2 years: Improvement-based scoring",
                "1 year: Industry percentile (max 80 points)",
                "No COVID weighting needed",
              ]}
            />

            <ComponentCard
              name="2. Current Performance"
              weight={23}
              icon={<BarChartIcon />}
              color="#4caf50"
              description="Industry benchmarking with lower peer threshold (10+ companies vs UK's 30)."
              details={[
                "Peer group minimum: 10 companies (adjusted for smaller dataset)",
                "Industry percentile ranking",
                "Same absolute adjustments as UK",
              ]}
            />

            <ComponentCard
              name="3. Industry Leadership"
              weight={17}
              icon={<BusinessCenterIcon />}
              color="#ff9800"
              description="Sector benchmarking without COVID bonus (post-pandemic data only)."
              details={[
                "Q1 performance: 90 points",
                "Median performance: 70 points",
                "Below median: 40 points",
                "Peer threshold: 10+ companies",
              ]}
            />

            <ComponentCard
              name="4. Structural Equity"
              weight={12}
              icon={<BalanceIcon />}
              color="#9c27b0"
              description="Identical to UK: Bonus participation (40%) + Quartile consistency (60%)."
              details={[
                "Bonus Participation: Equal access to bonuses",
                "Quartile Consistency: Balanced gender distribution",
                "Same scoring tiers as UK",
              ]}
            />

            <ComponentCard
              name="5. Reporting Quality"
              weight={10}
              icon={<VerifiedUserIcon />}
              color="#00bcd4"
              description="Simplified years-based scoring (no employer size or deadline data available)."
              details={[
                "Base score = (years_reported / 3) × 80",
                "Complete Reporting (3 years): 100 points (+20 bonus)",
                "Good Reporting (2 years): ~53 points",
                "Gap penalty: -10 points for non-consecutive years",
              ]}
            />

            <ComponentCard
              name="6. Creative Signals"
              weight={6}
              icon={<LightbulbIcon />}
              color="#607d8b"
              description="URL-focused transparency indicators (no leadership or deadline data)."
              details={[
                "URL Quality: Simple (≤5 slashes, +15pts), Transparency keywords (+12pts)",
                "PDF reports (+8pts), Company-hosted (+5pts)",
                "Reporting Consistency: 3 years (+10pts)",
                "Data Completeness: Bonus data disclosed (+5pts)",
              ]}
            />

            <Box sx={{ mt: 3, p: 2, bgcolor: "#e3f2fd", borderRadius: 2 }}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: "bold", mb: 1 }}
              >
                Key Differences from UK:
              </Typography>
              <List dense>
                <ListItem sx={{ py: 0 }}>
                  <ListItemText primary="• Higher component weights to compensate for fewer components" />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                  <ListItemText primary="• No Leadership Engagement/Stability (data unavailable)" />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                  <ListItemText primary="• Lower industry peer threshold (10 vs 30 companies)" />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                  <ListItemText primary="• Simplified Reporting Quality (years-based only)" />
                </ListItem>
              </List>
            </Box>
          </CardContent>
        </TabPanel>

        {/* France Tab */}
        <TabPanel
          value={tabValue}
          index={2}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography
              variant="h5"
              sx={{ fontWeight: "bold", mb: 3 }}
            >
              France Methodology
            </Typography>

            <Alert
              severity="warning"
              sx={{ mb: 3 }}
            >
              <strong>Fundamentally Different:</strong> France uses the
              government's mandatory Index Égalité score (already calculated
              0-100) plus a simple trend adjustment. No component-based scoring.
            </Alert>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 3 }}
            >
              France has mandatory government scoring for companies with 50+
              employees. Every company receives an official
              <strong> Index Égalité</strong> score that already measures pay
              gaps, promotions, salary increases, maternity bonuses, and top
              earner representation.
            </Typography>

            <Box
              sx={{
                mb: 4,
                p: 3,
                bgcolor: "#fff3e0",
                borderRadius: 2,
                border: "1px solid #ffb74d",
              }}
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: "bold", mb: 2 }}
              >
                Final Score Formula
              </Typography>
              <Typography
                variant="h5"
                sx={{ fontFamily: "monospace", textAlign: "center", my: 2 }}
              >
                FinalCompositeScore = CurrentScore + TrendAdjustment
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                align="center"
              >
                Capped at [0, 100]
              </Typography>
            </Box>

            <ComponentCard
              name="Element 1: Current Score"
              weight={100}
              icon={<BarChartIcon />}
              color="#2196f3"
              description="Most recent government Index Égalité score extracted from official data (2018-2024)."
              details={[
                "Source: French government's mandatory Index Égalité",
                "Cascade through years (newest first): 2024 → 2023 → ... → 2018",
                "Already incorporates: Pay gaps, Promotions, Salary increases, Maternity bonuses, Top earners",
                "Government formula is fixed (we cannot modify)",
              ]}
            />

            <ComponentCard
              name="Element 2: Trend Adjustment"
              weight={0}
              icon={<TrendingUpIcon />}
              color="#4caf50"
              description="±5 point bonus/penalty based on improvement trajectory."
              details={[
                "Requires 2+ years of data",
                "Change = Latest Score - Earliest Score",
                "Improved (change ≥5): +5 points",
                "Declined (change ≤-5): -5 points",
                "Stable (change between -5 and +5): 0 points",
                "Single year data: 0 adjustment (cannot assess trend)",
              ]}
            />

            <Divider sx={{ my: 4 }} />

            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", mb: 2 }}
            >
              Why France Is Different
            </Typography>
            <Grid
              container
              spacing={3}
            >
              <Grid
                item
                xs={12}
                md={6}
              >
                <Box sx={{ p: 2, bgcolor: "#e8f5e9", borderRadius: 2 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: "bold", mb: 1, color: "#2e7d32" }}
                  >
                    Government-Driven System
                  </Typography>
                  <List dense>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText primary="• Index Égalité is mandatory for 50+ employees" />
                    </ListItem>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText primary="• Government calculates official 0-100 score" />
                    </ListItem>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText primary="• Companies must publish and act if <75" />
                    </ListItem>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText primary="• Penalties for non-compliance" />
                    </ListItem>
                  </List>
                </Box>
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
              >
                <Box sx={{ p: 2, bgcolor: "#e3f2fd", borderRadius: 2 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: "bold", mb: 1, color: "#1976d2" }}
                  >
                    Our Value-Add
                  </Typography>
                  <List dense>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText primary="• Trend tracking across 7 years (2018-2024)" />
                    </ListItem>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText primary="• Cross-country comparison (vs UK & Ireland)" />
                    </ListItem>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText primary="• Unified A-F grading system" />
                    </ListItem>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText primary="• Historical analysis (not just snapshots)" />
                    </ListItem>
                  </List>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </TabPanel>
      </Card>

      {/* Glossary */}
      <Card
        elevation={3}
        sx={{ mt: 4, borderRadius: 3 }}
      >
        <CardContent sx={{ p: 3 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", mb: 3 }}
          >
            Glossary
          </Typography>
          <Grid
            container
            spacing={2}
          >
            {[
              {
                term: "Composite Gap",
                def: "Weighted average of 4 pay gap metrics (mean/median × hourly/bonus)",
              },
              {
                term: "Industry Percentile",
                def: "Company's rank within same industry (0-100, higher = better equity)",
              },
              {
                term: "Percentile-Based Grading",
                def: "Letter grades assigned based on relative rank, not absolute score",
              },
              {
                term: "SIC Code",
                def: "Standard Industrial Classification - numeric (UK) or text (Ireland/France)",
              },
              {
                term: "Index Égalité",
                def: "French government's mandatory 0-100 pay equity score",
              },
              {
                term: "Fallback Methodology",
                def: "Simplified scoring for UK companies without valid industry codes",
              },
              {
                term: "Trend Adjustment",
                def: "Bonus/penalty based on improvement trajectory",
              },
              {
                term: "COVID Weighting",
                def: "UK: 2020-21 years weighted at 0.7x in trend analysis",
              },
            ].map((item, i) => (
              <Grid
                item
                xs={12}
                md={6}
                key={i}
              >
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "#f5f5f5",
                    borderRadius: 1,
                    height: "100%",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: "bold", mb: 0.5 }}
                  >
                    {item.term}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    {item.def}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Methodology;
